import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { apiUrl, responseError, websocketUrl } from "../app/api";
import {
  canRequestCaptureSeal,
  captureLedgerDisposition
} from "../app/capture-integrity";
import { DEMO_TRANSCRIPT } from "../app/demo";
import {
  clearActiveCapture,
  readActiveCapture,
  writeActiveCapture
} from "../app/storage";
import {
  canonicalRecorderMimeType,
  captureStartErrorMessage,
  preferredRecorderMimeType,
  StreamingLinearResampler
} from "../app/audio-processing";
import type {
  CaptureState,
  MatterDraft,
  ReviewReason,
  SourceAudioStatus,
  TranscriptRow,
  TranscriptSession
} from "../app/ui-types";
import {
  CaptureBufferRuntime,
  createRecordingBuffer,
  type RecordingStorageDegradation
} from "../services";
import {
  activeElapsedMilliseconds,
  silenceGuardState,
  VoiceActivityDetector
} from "../app/silence-guard";
import { FEATURE_FLAGS } from "../app/feature-flags";
import { coalesceCaptureGap } from "../app/capture-gaps";
import { sendCaptureTelemetry, sendEntryTelemetry } from "../app/telemetry";
import {
  canStartRecording,
  canStopRecording,
  captureSealPending,
  initialRecordingLifecycle,
  MAX_FINALISATION_ATTEMPTS,
  mergeOrderedPackets,
  nextReconnect,
  recordingLifecycleReducer,
  type RecordingStartupStage
} from "../app/recording-lifecycle";
import {
  applyLiveTranscriptEvent,
  orderTranscriptRows
} from "../app/live-transcript";

export { orderTranscriptRows } from "../app/live-transcript";

type ChunkRecord = {
  sequence: number;
  capturedAt: string;
  hash: string;
  blob: Blob;
};

type PcmPacket = {
  type: "audio.append";
  sequence: number;
  startMs: number;
  endMs: number;
  capturedAt: string;
  audioBase64: string;
};

type SourcePacket = {
  type: "source.append";
  sequence: number;
  startMs: number;
  endMs: number;
  capturedAt: string;
  mimeType: string;
  audioBase64: string;
};

type CaptureGap = {
  startMs: number;
  endMs: number;
  reason: string;
};

type WakeLockSentinelLike = {
  release: () => Promise<void>;
};

export type RecoveryFinalisationOutcome =
  | "checking"
  | "preparing-review"
  | "local-evidence"
  | "server-retry"
  | "recovering-local-evidence"
  | "review-ready"
  | "unavailable"
  | "quarantined"
  | "error";

const recordRecoveryDiagnostic = (entry: {
  recordingId: string;
  resultingState: RecoveryFinalisationOutcome;
  errorCategory?: string;
  requestId?: string | null;
}) => {
  console.info(
    JSON.stringify({
      event: "recording-recovery-decision",
      recordingId: entry.recordingId,
      resultingState: entry.resultingState,
      errorCategory: entry.errorCategory ?? null,
      requestId: entry.requestId ?? null
    })
  );
};

type ServerTranscriptNode = {
  id: string;
  ordinal: number;
  kind?: TranscriptRow["kind"];
  text: string;
  sourceNodeId?: string | null;
  timeRange: { startMs: number; endMs: number };
  speaker: {
    speakerId: string | null;
    label: string;
    confidence: number | null;
  } | null;
  confidence: {
    wordConfidence: number | null;
    speakerConfidence: number | null;
    alignmentConfidence: number | null;
    mismatch: boolean;
    overlap: boolean;
  };
};

type ServerTranscript = {
  transcriptId: string;
  recordingId: string;
  baseline: { nodes: ServerTranscriptNode[] };
  projection: { nodes: ServerTranscriptNode[] };
  version: {
    id: string;
    versionNumber: number;
    state: "draft" | "frozen";
  };
};

type ServerRecordingView = {
  recording?: { status?: string; matterId?: string };
  manifest?: unknown;
  sourceAudio?: SourceAudioStatus;
};

type LiveMessage = {
  type: string;
  provider?: "openai" | "deepgram" | "mock" | "dicta";
  category?: "optional-sidecar" | "canonical-live-text" | "finalisation";
  code?: string;
  row?: TranscriptRow;
  sequence?: number;
  capturedAt?: string;
  safeThroughMs?: number;
  state?: CaptureState;
  status?: string;
  providerMode?: "mock" | "live";
  transcriptId?: string;
  provisionalRowId?: string;
  message?: string;
  expiresAt?: string;
};

export function liveProviderNotice(
  provider: LiveMessage["provider"],
  category?: LiveMessage["category"]
): string {
  return category === "optional-sidecar" || (!category && provider === "deepgram")
    ? "Live speaker labels and confidence are delayed. Recording and transcript continue."
    : "Live text is delayed. Source capture continues.";
}

export type LiveShare = {
  id: string;
  url: string;
  createdAt: string;
  expiresAt: string | null;
};

export type CaptureStartupStage = RecordingStartupStage;

const STAGING_DEMO = import.meta.env.VITE_STAGING_DEMO === "true";

const TERMINAL_CAPTURE_ERROR_CODES = new Set([
  "capture_incomplete",
  "capture_boundary_mismatch",
  "capture_metadata_mismatch",
  "capture_end_mismatch",
  "source_mime_conflict",
  "source_chunk_conflict",
  "chunk_conflict",
  "capture_commit_conflict",
  "capture_stream_mismatch",
  "capture_not_committed",
  "capture_committed"
]);

const proceedingType: Record<MatterDraft["proceeding"], string> = {
  "Deposition / Examination": "deposition-examination",
  "Investigation / Interview": "investigation-interview",
  Conference: "conference",
  Hearing: "hearing"
};

const languageCode = (value: string) => {
  const language = value.trim().toLowerCase();
  if (language === "english") return "en";
  if (language === "spanish") return "es";
  if (language === "french") return "fr";
  if (language === "mandarin" || language === "chinese") return "zh";
  return language.slice(0, 16) || "und";
};

const participantRole = (value: string) => {
  const role = value.toLowerCase();
  if (role.includes("witness")) return "witness";
  if (role.includes("interpreter")) return "interpreter";
  if (role.includes("judge")) return "judge";
  if (role.includes("registrar")) return "registrar";
  if (role.includes("investigator")) return "investigator";
  if (role.includes("interviewer")) return "interviewer";
  if (role.includes("interviewee")) return "interviewee";
  if (role.includes("examining") || role.includes("examiner")) return "examiner";
  if (role.includes("counsel") || role.includes("solicitor")) return "counsel";
  return "other";
};

const scheduledAt = (date: string, timezone: string) => {
  const parts = date.split("-").map(Number);
  if (
    parts.length !== 3 ||
    parts.some((part) => !Number.isInteger(part)) ||
    !timezone.trim()
  ) {
    return new Date().toISOString();
  }
  const [year, month, day] = parts as [number, number, number];
  const targetLocal = Date.UTC(year, month - 1, day, 12, 0, 0);
  let guess = targetLocal;
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23"
    });
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const values = Object.fromEntries(
        formatter
          .formatToParts(new Date(guess))
          .filter((part) => part.type !== "literal")
          .map((part) => [part.type, Number(part.value)])
      );
      const representedLocal = Date.UTC(
        values.year,
        values.month - 1,
        values.day,
        values.hour,
        values.minute,
        values.second
      );
      guess += targetLocal - representedLocal;
    }
    return new Date(guess).toISOString();
  } catch {
    return new Date(targetLocal).toISOString();
  }
};

const toPcm16 = (input: Float32Array) => {
  const output = new Int16Array(input.length);
  for (let index = 0; index < input.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, input[index]));
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
};

const toBase64 = (input: Uint8Array) => {
  let binary = "";
  for (let index = 0; index < input.byteLength; index += 1) {
    binary += String.fromCharCode(input[index]);
  }
  return window.btoa(binary);
};

const digestBytes = async (bytes: ArrayBuffer) => {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
};

const percentage = (value: number | null | undefined) =>
  Math.max(0, Math.min(100, Math.round((value ?? 0) * 100)));

const releaseAudioContext = (reference: { current: AudioContext | null }) => {
  const context = reference.current;
  reference.current = null;
  if (!context || context.state === "closed") return;
  void context.close().catch(() => undefined);
};

const reviewReasons = (node: ServerTranscriptNode): ReviewReason[] => [
  ...(percentage(node.confidence.wordConfidence) < 65
    ? (["low-confidence"] as ReviewReason[])
    : []),
  ...(percentage(node.confidence.speakerConfidence) < 65
    ? (["uncertain-speaker"] as ReviewReason[])
    : []),
  ...(node.confidence.mismatch ? (["provider-disagreement"] as ReviewReason[]) : []),
  ...(node.confidence.overlap ? (["overlap"] as ReviewReason[]) : [])
];

const toTranscriptRows = (transcript: ServerTranscript): TranscriptRow[] => {
  const baselineById = new Map(transcript.baseline.nodes.map((node) => [node.id, node]));
  return transcript.projection.nodes.map((node, index) => {
    const baseline =
      baselineById.get(node.sourceNodeId ?? node.id)?.text ??
      baselineById.get(node.id)?.text ??
      node.text;
    const wordConfidence = percentage(node.confidence.wordConfidence);
    const speakerConfidence = percentage(node.confidence.speakerConfidence);
    const alignmentConfidence = percentage(node.confidence.alignmentConfidence);
    return {
      id: node.sourceNodeId ?? node.id,
      kind: (node.kind ?? "speech") as TranscriptRow["kind"],
      line: index + 1,
      startMs: node.timeRange.startMs,
      endMs: node.timeRange.endMs,
      speakerId: node.speaker?.speakerId ?? "unknown",
      speaker: node.speaker?.label ?? "Unknown speaker",
      role: node.speaker?.label ?? "RECORDER",
      baseline,
      text: node.text,
      wordConfidence,
      speakerConfidence,
      alignmentConfidence,
      reviewScore: Math.round(
        (wordConfidence + speakerConfidence + alignmentConfidence) / 3
      ),
      reasons: reviewReasons(node),
      provisional: false
    };
  });
};

// workspaceId is intentionally omitted: the worker scopes tenancy by the
// authenticated user (created_by), and defaults the workspace server-side.
const makeRecordingBody = (matter: MatterDraft, deviceLabel: string) => ({
  matter: {
    name: matter.title,
    reference: matter.reference,
    proceedingType: proceedingType[matter.proceeding],
    jurisdictionPackId: matter.jurisdictionPack,
    venue: matter.venue,
    scheduledAt: scheduledAt(matter.date, matter.timezone),
    timezone: matter.timezone,
    expectedLanguages: matter.languages.map(languageCode),
    glossary: matter.glossary,
    confidentialityNote: matter.authorityReference,
    legalHold: false
  },
  participants: matter.participants.map((participant) => ({
    displayName: participant.name,
    role: participantRole(participant.role),
    preferredSpeakerLabel: participant.name,
    expectedLanguages: matter.languages.map(languageCode)
  })),
  authorityEvidence: {
    authorityAttested: matter.authority.userAttestation,
    participantNoticeGiven: matter.authority.participantNotice,
    participantConsentObtained: matter.authority.participantConsent,
    confidentialityChecked: matter.authority.confidentialityReviewed,
    courtApprovalObtained: matter.authority.courtApproval,
    supportingReference: matter.authorityReference
  },
  deviceLabel
});

export function useLiveSession(
  matter: MatterDraft,
  onSessionChange: (session: TranscriptSession) => void,
  onCaptureStateChange?: (state: CaptureState) => void,
  options: { sandbox?: boolean; matterId?: string; previousSessionCount?: number } = {}
) {
  const sandbox = options.sandbox === true;
  const matterId = options.matterId;
  const demoCapture = STAGING_DEMO || sandbox;
  const [lifecycle, dispatchLifecycle] = useReducer(
    recordingLifecycleReducer,
    initialRecordingLifecycle
  );
  const {
    captureState: state,
    connection,
    error,
    isStarting,
    startupStage
  } = lifecycle;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [rows, setRows] = useState<TranscriptRow[]>([]);
  const [sourceSafeThroughMs, setSourceSafeThroughMs] = useState(0);
  const [analysisSafeThroughMs, setAnalysisSafeThroughMs] = useState(0);
  const [bufferedChunks, setBufferedChunks] = useState(0);
  const [analysisBufferedChunks, setAnalysisBufferedChunks] = useState(0);
  const [gaps, setGaps] = useState<CaptureGap[]>([]);
  const [transcriptSession, setTranscriptSession] = useState<TranscriptSession | null>(
    null
  );
  const [sourceAudioUrl, setSourceAudioUrl] = useState<string | null>(null);
  const [recoveryAvailable, setRecoveryAvailable] = useState(false);
  const [recoveryStatus, setRecoveryStatus] =
    useState<RecoveryFinalisationOutcome>("checking");
  const [recoveryDownloads, setRecoveryDownloads] = useState({
    source: false,
    analysis: false
  });
  const [recoveryCheckNonce, setRecoveryCheckNonce] = useState(0);
  const [storageWarning, setStorageWarning] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [pauseWarning, setPauseWarning] = useState(false);
  const [pauseElapsedSeconds, setPauseElapsedSeconds] = useState(0);
  const [silenceWarning, setSilenceWarning] = useState(false);
  const [silenceSecondsRemaining, setSilenceSecondsRemaining] = useState(60);
  const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null);
  const [liveShare, setLiveShare] = useState<LiveShare | null>(null);
  const [liveShareBusy, setLiveShareBusy] = useState(false);

  const sourceChunks = useRef<ChunkRecord[]>([]);
  const sourceAudioObjectUrl = useRef<string | null>(null);
  const evidenceBuffer = useRef<CaptureBufferRuntime | null>(null);
  const activeRecordingId = useRef("");
  const activeTranscriptId = useRef("");
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const socket = useRef<WebSocket | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const processor = useRef<AudioWorkletNode | null>(null);
  const wakeLock = useRef<WakeLockSentinelLike | null>(null);
  const hiddenAt = useRef<number | null>(null);
  const timer = useRef<number | null>(null);
  const mockTimer = useRef<number | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectExhausted = useRef(false);
  const finaliseTimer = useRef<number | null>(null);
  const finalisationAttempts = useRef(0);
  const finalisationStartedAt = useRef(0);
  const pauseWarningTimer = useRef<number | null>(null);
  const pauseEndTimer = useRef<number | null>(null);
  const pauseClockTimer = useRef<number | null>(null);
  const startedAt = useRef<number>(0);
  const pausedAt = useRef<number>(0);
  const pauseTimelineAt = useRef<number>(0);
  const pauseReason = useRef<"manual" | "silence" | "adjournment">("manual");
  const accumulatedPausedMs = useRef(0);
  const lastSpeechAt = useRef(0);
  const silenceNotificationShown = useRef(false);
  const voiceActivity = useRef(new VoiceActivityDetector());
  const activeSessionOrdinal = useRef((options.previousSessionCount ?? 0) + 1);
  const pausedRef = useRef(false);
  const captureState = useRef<CaptureState>("ready");
  const shouldReconnect = useRef(false);
  const pendingStop = useRef(false);
  const stopSent = useRef(false);
  const analysisCommitted = useRef(false);
  const sourceCommitted = useRef(false);
  const captureProducersQuiesced = useRef(false);
  const mockCursor = useRef(0);
  const pcmSequence = useRef(0);
  const pcmCursorMs = useRef(0);
  const sourceSequence = useRef(0);
  const sourceCursorMs = useRef(0);
  const sourceMimeType = useRef("audio/webm");
  const pcmWriteChain = useRef<Promise<void>>(Promise.resolve());
  const sourceWriteChain = useRef<Promise<void>>(Promise.resolve());
  const startInFlight = useRef(false);
  const startupCancelled = useRef(false);
  const stopRef = useRef<(() => void) | null>(null);
  const pauseRef = useRef<((reason?: "manual" | "silence") => void) | null>(null);
  const connectSocketRef = useRef<
    ((recordingId: string, transcriptId: string) => void) | null
  >(null);
  const pendingControls = useRef<object[]>([]);
  const pendingPcmPackets = useRef<Map<number, PcmPacket>>(new Map());
  const pendingSourcePackets = useRef<Map<number, SourcePacket>>(new Map());
  const captureAttemptId = useRef(crypto.randomUUID());
  const audioPacketsSent = useRef(0);
  const transcriptFramesReceived = useRef(0);
  const firstAudioReported = useRef(false);
  const firstTranscriptReported = useRef(false);
  const defaultSpeakerName = useRef(matter.participants[0]?.name.trim() ?? "");
  const defaultSpeakerCluster = useRef<string | null>(null);
  const providerReady = useRef(false);
  const firstAudioAcknowledged = useRef(false);
  const captureReadyResolve = useRef<(() => void) | null>(null);
  const captureReadyReject = useRef<((cause: Error) => void) | null>(null);

  const setConnection = useCallback((next: typeof connection) => {
    dispatchLifecycle({ type: "connection.changed", connection: next });
  }, []);

  const setError = useCallback((message: string | null) => {
    dispatchLifecycle({ type: "error.changed", message });
  }, []);

  const reportStorageDegraded = useCallback(
    (failure: RecordingStorageDegradation) => {
      setStorageWarning(
        "Device recovery storage is unavailable. Live recording continues, and Adjourn and End session remain available. Keep this tab open until Review appears."
      );
      sendCaptureTelemetry("client.storage.degraded", {
        captureAttemptId: captureAttemptId.current,
        ...(activeRecordingId.current
          ? { recordingId: activeRecordingId.current }
          : {}),
        stream: failure.stream,
        ...(failure.sequence === undefined ? {} : { sequence: failure.sequence }),
        errorCode: failure.errorCode
      });
    },
    []
  );

  const openEvidenceBuffer = useCallback(async () => {
    if (evidenceBuffer.current) return evidenceBuffer.current;
    const runtime = new CaptureBufferRuntime(
      await createRecordingBuffer(),
      reportStorageDegraded
    );
    evidenceBuffer.current = runtime;
    return runtime;
  }, [reportStorageDegraded]);

  const updateState = useCallback((next: CaptureState) => {
    captureState.current = next;
    dispatchLifecycle({ type: "capture.changed", state: next });
  }, []);

  useEffect(() => {
    defaultSpeakerName.current = matter.participants[0]?.name.trim() ?? "";
  }, [matter.participants]);

  const markCaptureReady = useCallback(() => {
    if (!providerReady.current || !firstAudioAcknowledged.current) return;
    captureState.current = "listening";
    reconnectAttempts.current = 0;
    reconnectExhausted.current = false;
    finalisationAttempts.current = 0;
    dispatchLifecycle({ type: "capture.ready" });
    captureReadyResolve.current?.();
    captureReadyResolve.current = null;
    captureReadyReject.current = null;
  }, []);

  const availableRecoveryLedgers = useCallback(
    () => ({
      source: sourceChunks.current.length > 0 || bufferedChunks > 0,
      analysis: analysisBufferedChunks > 0
    }),
    [analysisBufferedChunks, bufferedChunks]
  );

  const recoveryEvidence = availableRecoveryLedgers();
  const recoveryDownloaded =
    (recoveryEvidence.source || recoveryEvidence.analysis) &&
    (!recoveryEvidence.source || recoveryDownloads.source) &&
    (!recoveryEvidence.analysis || recoveryDownloads.analysis);

  const activeElapsedMs = (now = Date.now()) =>
    activeElapsedMilliseconds(
      startedAt.current,
      now,
      accumulatedPausedMs.current,
      pausedRef.current ? pausedAt.current : 0
    );

  const closeSilenceNotification = useCallback(() => {
    silenceNotificationShown.current = false;
    if (!("serviceWorker" in navigator)) return;
    void navigator.serviceWorker.ready
      .then((registration) => registration.getNotifications({ tag: "dicta-silence" }))
      .then((notifications) => notifications.forEach((notification) => notification.close()))
      .catch(() => undefined);
  }, []);

  const markSpeechActive = useCallback(() => {
    lastSpeechAt.current = Date.now();
    setSilenceWarning(false);
    setSilenceSecondsRemaining(60);
    closeSilenceNotification();
  }, [closeSilenceNotification]);

  const checkSilenceDeadline = useCallback(() => {
    if (!FEATURE_FLAGS.silenceGuard) return;
    if (
      captureState.current !== "listening" ||
      pausedRef.current ||
      lastSpeechAt.current <= 0
    ) {
      return;
    }
    const guard = silenceGuardState(lastSpeechAt.current);
    if (guard.phase === "recording") return;
    setSilenceWarning(true);
    setSilenceSecondsRemaining(guard.secondsRemaining);
    if (silenceNotificationShown.current || !("serviceWorker" in navigator)) return;
    silenceNotificationShown.current = true;
    void navigator.serviceWorker.ready
      .then((registration) =>
        registration.showNotification("Still recording?", {
          body: "No speech detected for one minute. Continue or pause Dicta.",
          tag: "dicta-silence",
          silent: true,
          actions: [
            { action: "continue", title: "Continue" },
            { action: "pause", title: "Pause" }
          ],
          data: { type: "dicta:silence" }
        } as NotificationOptions & {
          actions: Array<{ action: string; title: string }>;
        })
      )
      .catch(() => undefined);
  }, [closeSilenceNotification]);

  const publishSourceBlob = useCallback((blob: Blob): string => {
    if (sourceAudioObjectUrl.current) {
      URL.revokeObjectURL(sourceAudioObjectUrl.current);
    }
    const url = URL.createObjectURL(blob);
    sourceAudioObjectUrl.current = url;
    setSourceAudioUrl(url);
    return url;
  }, []);

  const buildSourceBlob = useCallback(() => {
    if (!sourceChunks.current.length) return null;
    const ordered = [...sourceChunks.current].sort(
      (left, right) => left.sequence - right.sequence
    );
    return new Blob(
      ordered.map((chunk) => chunk.blob),
      {
        type:
          sourceMimeType.current ||
          ordered[0]?.blob.type ||
          mediaRecorder.current?.mimeType ||
          "audio/webm"
      }
    );
  }, []);

  const clearPersistedRecordingAudio = useCallback(async (recordingId: string) => {
    const buffer = evidenceBuffer.current;
    if (buffer) {
      await Promise.all([
        buffer.clearRecording(recordingId),
        buffer.clearRecording(`analysis:${recordingId}`)
      ]);
    }
    setBufferedChunks(0);
    setAnalysisBufferedChunks(0);
    setStorageWarning(null);
  }, []);

  const clearLocalRecordingAudio = useCallback(async (recordingId: string) => {
    await clearPersistedRecordingAudio(recordingId);
    sourceChunks.current = [];
    if (sourceAudioObjectUrl.current) {
      URL.revokeObjectURL(sourceAudioObjectUrl.current);
      sourceAudioObjectUrl.current = null;
    }
    setSourceAudioUrl(null);
  }, [clearPersistedRecordingAudio]);

  const loadFinalTranscript = useCallback(
    async (transcriptId: string, audioUrl?: string) => {
      const response = await fetch(apiUrl(`/api/transcripts/${encodeURIComponent(transcriptId)}`));
      if (!response.ok) throw await responseError(response, "Final transcript could not be loaded.");
      const transcript = (await response.json()) as ServerTranscript;
      const recordingResponse = await fetch(
        apiUrl(`/api/recordings/${encodeURIComponent(transcript.recordingId)}`)
      );
      const recordingView = recordingResponse.ok
        ? ((await recordingResponse.json()) as ServerRecordingView)
        : null;
      const sourceAudio = recordingView?.sourceAudio;
      let sourceAudioReviewable = false;
      if (sourceAudio && sourceAudio.state !== "deleted") {
        const audioResponse = await fetch(
          apiUrl(
            `/api/recordings/${encodeURIComponent(transcript.recordingId)}/audio`
          ),
          {
            credentials: "same-origin",
            headers: { Range: "bytes=0-0" }
          }
        ).catch(() => null);
        sourceAudioReviewable =
          audioResponse?.status === 200 || audioResponse?.status === 206;
        await audioResponse?.arrayBuffer().catch(() => undefined);
      }
      if (sourceAudioReviewable) {
        await clearPersistedRecordingAudio(transcript.recordingId);
      }
      if (
        sourceAudioReviewable &&
        sourceAudio?.retentionPolicy === "delete-within-24h"
      ) {
        await clearLocalRecordingAudio(transcript.recordingId);
        audioUrl = undefined;
      }
      const nextRows = toTranscriptRows(transcript);
      setRows(nextRows);
      sendCaptureTelemetry("client.capture.completed", {
        captureAttemptId: captureAttemptId.current,
        recordingId: transcript.recordingId,
        elapsedMs: Math.max(0, Date.now() - startedAt.current),
        audioPacketsSent: audioPacketsSent.current,
        transcriptFramesReceived: transcriptFramesReceived.current,
        rowsDisplayed: nextRows.length
      });
      if (localStorage.getItem("dicta-first-recording-reported") !== "true") {
        localStorage.setItem("dicta-first-recording-reported", "true");
        sendEntryTelemetry("client.entry.first_recording_completed");
      }
      if (transcriptFramesReceived.current > 0 && nextRows.length === 0) {
        sendCaptureTelemetry("client.transcript_not_displayed", {
          captureAttemptId: captureAttemptId.current,
          recordingId: transcript.recordingId,
          transcriptFramesReceived: transcriptFramesReceived.current,
          rowsDisplayed: 0
        });
      }
      const resolvedSessionMatterId = matterId ?? readActiveCapture()?.matterId;
      const session: TranscriptSession = {
        ...(resolvedSessionMatterId ? { matterId: resolvedSessionMatterId } : {}),
        sessionOrdinal: activeSessionOrdinal.current,
        recordingId: transcript.recordingId,
        transcriptId: transcript.transcriptId,
        versionId: transcript.version.id,
        versionNumber: transcript.version.versionNumber,
        state: transcript.version.state,
        recordedAtEpochMs: startedAt.current || undefined,
        ...(sourceAudio ? { sourceAudio } : {}),
        ...(sourceAudio?.retentionPolicy !== "delete-within-24h" &&
        (audioUrl ?? sourceAudioUrl)
          ? { audioUrl: audioUrl ?? sourceAudioUrl ?? undefined }
          : {})
      };
      setTranscriptSession(session);
      if (!sandbox) {
        onSessionChange(session);
        clearActiveCapture();
      }
      setRecoveryAvailable(false);
      setRecoveryStatus("review-ready");
      recordRecoveryDiagnostic({
        recordingId: transcript.recordingId,
        resultingState: "review-ready"
      });
      updateState("review");
      shouldReconnect.current = false;
      setConnection("standing-by");
      socket.current?.close();
      return session;
    },
    [
      clearLocalRecordingAudio,
      clearPersistedRecordingAudio,
      matterId,
      onSessionChange,
      sandbox,
      sourceAudioUrl,
      updateState
    ]
  );

  const beginMockTranscript = useCallback((ws: WebSocket) => {
    if (mockTimer.current || mockCursor.current >= DEMO_TRANSCRIPT.length) return;
    mockTimer.current = window.setInterval(() => {
      const row = DEMO_TRANSCRIPT[mockCursor.current];
      if (
        !row ||
        ws.readyState !== WebSocket.OPEN ||
        captureState.current !== "listening" ||
        pausedRef.current
      ) {
        if (!row && mockTimer.current) {
          window.clearInterval(mockTimer.current);
          mockTimer.current = null;
        }
        return;
      }
      ws.send(
        JSON.stringify({
          type: "mock.transcript",
          text: row.text,
          speaker: row.speaker,
          confidence: row.wordConfidence / 100,
          startMs: row.startMs,
          endMs: row.endMs
        })
      );
      mockCursor.current += 1;
    }, 1800);
  }, []);

  const beginStagingDemoTranscript = useCallback(() => {
    if (mockTimer.current || mockCursor.current >= DEMO_TRANSCRIPT.length) return;
    mockTimer.current = window.setInterval(() => {
      const row = DEMO_TRANSCRIPT[mockCursor.current];
      if (!row || captureState.current !== "listening" || pausedRef.current) {
        if (!row && mockTimer.current) {
          window.clearInterval(mockTimer.current);
          mockTimer.current = null;
        }
        return;
      }
      setRows((current) => [
        ...current,
        {
          ...row,
          id: `staging-${row.id}`,
          line: current.length + 1,
          provisional: false
        }
      ]);
      mockCursor.current += 1;
    }, 1800);
  }, []);

  const flushBufferedPcm = useCallback(async (ws: WebSocket, recordingId: string) => {
    const buffer = evidenceBuffer.current;
    // Replay declarations before their chunks. A reconnect can otherwise put
    // the timestamp discontinuity in the ledger before its gap evidence.
    const controls = pendingControls.current.splice(0);
    for (let index = 0; index < controls.length; index += 1) {
      const control = controls[index];
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(control));
      } else {
        pendingControls.current.unshift(...controls.slice(index));
        return;
      }
    }
    const chunks = buffer
      ? await buffer.listChunks(`analysis:${recordingId}`, "analysis")
      : [];
    const storedPackets: PcmPacket[] = chunks
      .filter((chunk) => chunk.uploadState !== "uploaded")
      .map((chunk) => ({
        type: "audio.append",
        sequence: chunk.sequence,
        startMs: chunk.startMs,
        endMs: chunk.endMs,
        capturedAt: chunk.capturedAt,
        audioBase64: toBase64(new Uint8Array(chunk.bytes))
      }));
    for (const packet of mergeOrderedPackets(
      storedPackets,
      pendingPcmPackets.current.values()
    )) {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify(packet));
    }
  }, []);

  const flushBufferedSource = useCallback(async (ws: WebSocket, recordingId: string) => {
    const buffer = evidenceBuffer.current;
    const chunks = buffer ? await buffer.listChunks(recordingId, "source") : [];
    const storedPackets: SourcePacket[] = chunks
      .filter((chunk) => chunk.uploadState !== "uploaded")
      .map((chunk) => ({
        type: "source.append",
        sequence: chunk.sequence,
        startMs: chunk.startMs,
        endMs: chunk.endMs,
        capturedAt: chunk.capturedAt,
        mimeType: chunk.mimeType,
        audioBase64: toBase64(new Uint8Array(chunk.bytes))
      }));
    for (const packet of mergeOrderedPackets(
      storedPackets,
      pendingSourcePackets.current.values()
    )) {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify(packet));
    }
  }, []);

  const sendSessionStopIfCommitted = useCallback((ws: WebSocket) => {
    if (
      !pendingStop.current ||
      stopSent.current ||
      ws.readyState !== WebSocket.OPEN ||
      !analysisCommitted.current ||
      !sourceCommitted.current
    ) {
      return;
    }
    ws.send(JSON.stringify({ type: "session.stop" }));
    stopSent.current = true;
  }, []);

  const sendStopWhenReady = useCallback(async () => {
    try {
      await Promise.all([pcmWriteChain.current, sourceWriteChain.current]);
      const ws = socket.current;
      if (!ws) return;
      if (!canRequestCaptureSeal({
        pendingStop: pendingStop.current,
        stopSent: stopSent.current,
        producersQuiesced: captureProducersQuiesced.current,
        socketOpen: ws.readyState === WebSocket.OPEN
      })) {
        return;
      }
      await Promise.all([
        flushBufferedPcm(ws, activeRecordingId.current),
        flushBufferedSource(ws, activeRecordingId.current)
      ]);
      ws.send(
        JSON.stringify({
          type: "audio.commit",
          expectedChunks: pcmSequence.current,
          endMs: pcmCursorMs.current
        })
      );
      ws.send(
        JSON.stringify({
          type: "source.commit",
          expectedChunks: sourceSequence.current,
          endMs: sourceCursorMs.current,
          mimeType: sourceMimeType.current
        })
      );
    } catch {
      setConnection("buffering");
      setError(
        "A buffered chunk could not be prepared for sealing. Dicta will retry; keep this tab open and preserve the source copy."
      );
    }
  }, [flushBufferedPcm, flushBufferedSource]);

  const connectSocket = useCallback(
    (recordingId: string, transcriptId: string) => {
      if (!shouldReconnect.current) return;
      const ws = new WebSocket(
        websocketUrl(`/api/recordings/${encodeURIComponent(recordingId)}/live`)
      );
      socket.current = ws;

      ws.addEventListener("open", () => {
        dispatchLifecycle({ type: "socket.connected" });
        setError(null);
        sendCaptureTelemetry("client.capture.connected", {
          captureAttemptId: captureAttemptId.current,
          recordingId,
          connectionState: "connected",
          elapsedMs: Math.max(0, Date.now() - startedAt.current)
        });
        ws.send(
          JSON.stringify({
            type: "session.start",
            recordingId,
            format: { type: "audio/pcm", rate: 24000 },
            models: {
              transcript: "gpt-live-transcribe",
              metadata: "deepgram-nova-3"
            }
          })
        );
        void Promise.all([
          flushBufferedPcm(ws, recordingId),
          flushBufferedSource(ws, recordingId)
        ])
          .then(sendStopWhenReady)
          .catch(() => {
            setConnection("buffering");
            setError(
              "Buffered evidence could not be read for upload. Dicta will reconnect and retry."
            );
            ws.close();
          });
      });

      ws.addEventListener("message", (event) => {
        let message: LiveMessage;
        try {
          message = JSON.parse(String(event.data)) as LiveMessage;
        } catch {
          setError("Live metadata could not be read. Source audio remains buffered.");
          return;
        }

        if (
          (message.type === "transcript.delta" || message.type === "transcript.commit") &&
          message.row
        ) {
          transcriptFramesReceived.current += 1;
          if (!firstTranscriptReported.current) {
            firstTranscriptReported.current = true;
            sendCaptureTelemetry("client.capture.first_transcript", {
              captureAttemptId: captureAttemptId.current,
              recordingId,
              elapsedMs: Math.max(0, Date.now() - startedAt.current),
              transcriptFramesReceived: transcriptFramesReceived.current
            });
          }
          const incomingRow = message.row;
          const rawSpeaker = incomingRow.speaker.trim();
          if (
            !defaultSpeakerCluster.current &&
            (incomingRow.kind ?? "speech") === "speech" &&
            rawSpeaker
          ) {
            defaultSpeakerCluster.current = rawSpeaker;
          }
          const row =
            defaultSpeakerName.current &&
            defaultSpeakerCluster.current === rawSpeaker &&
            (incomingRow.kind ?? "speech") === "speech"
              ? {
                  ...incomingRow,
                  speaker: defaultSpeakerName.current,
                  role: defaultSpeakerName.current
                }
              : incomingRow;
          const transcriptEventType =
            message.type === "transcript.delta"
              ? "transcript.delta"
              : "transcript.commit";
          setRows((current) =>
            applyLiveTranscriptEvent(current, {
              type: transcriptEventType,
              row,
              ...(message.provisionalRowId
                ? { provisionalRowId: message.provisionalRowId }
                : {})
            })
          );
          return;
        }

        if (message.type === "session.ready" && message.providerMode === "mock") {
          beginMockTranscript(ws);
          return;
        }

        if (message.type === "provider.ready") {
          providerReady.current = true;
          reconnectAttempts.current = 0;
          reconnectExhausted.current = false;
          dispatchLifecycle({ type: "provider.ready" });
          markCaptureReady();
          return;
        }

        if (message.type === "live-share.ended" && message.expiresAt) {
          setLiveShare((current) =>
            current ? { ...current, expiresAt: message.expiresAt ?? null } : current
          );
          return;
        }

        if (message.type === "audio.ack" && message.sequence !== undefined) {
          firstAudioAcknowledged.current = true;
          markCaptureReady();
          pendingPcmPackets.current.delete(message.sequence);
          if (message.safeThroughMs !== undefined) {
            setAnalysisSafeThroughMs(message.safeThroughMs);
          }
          void evidenceBuffer.current
            ?.markUploaded(
              `analysis:${recordingId}`,
              message.sequence,
              new Date().toISOString(),
              "analysis"
            )
            .catch(() => undefined);
          return;
        }

        if (message.type === "source.ack" && message.sequence !== undefined) {
          pendingSourcePackets.current.delete(message.sequence);
          if (message.safeThroughMs !== undefined) {
            setSourceSafeThroughMs(message.safeThroughMs);
          }
          void evidenceBuffer.current
            ?.markUploaded(
              recordingId,
              message.sequence,
              new Date().toISOString(),
              "source"
            )
            .catch(() => undefined);
          return;
        }

        if (message.type === "audio.committed") {
          analysisCommitted.current = true;
          sendSessionStopIfCommitted(ws);
          return;
        }

        if (message.type === "source.committed") {
          sourceCommitted.current = true;
          sendSessionStopIfCommitted(ws);
          return;
        }

        if (message.type === "session.state" && message.state) {
          if (message.state !== "review" && !pendingStop.current) {
            updateState(message.state);
          }
          return;
        }

        if (
          ((message.type === "recording.status" && message.status === "review") ||
            message.type === "recording.review") &&
          (message.transcriptId || transcriptId)
        ) {
          const blob = buildSourceBlob();
          const audioUrl = blob ? publishSourceBlob(blob) : undefined;
          void loadFinalTranscript(message.transcriptId ?? transcriptId, audioUrl).catch(
            (cause: unknown) => {
              setError(cause instanceof Error ? cause.message : "Final transcript could not load.");
            }
          );
          return;
        }

        if (
          (message.type === "recording.status" &&
            message.status === "interrupted") ||
          message.type === "recording.finalisation_failed" ||
          message.type === "capture.interrupted"
        ) {
          sendCaptureTelemetry("client.capture.failed", {
            captureAttemptId: captureAttemptId.current,
            recordingId,
            errorCode: message.code ?? "finalisation_failed",
            elapsedMs: Math.max(0, Date.now() - startedAt.current),
            audioPacketsSent: audioPacketsSent.current,
            transcriptFramesReceived: transcriptFramesReceived.current
          });
          shouldReconnect.current = false;
          stopSent.current = false;
          setConnection("standing-by");
          setError(
            message.message ??
              "Final transcript could not be prepared. Source evidence remains available. Retry preparing Review."
          );
          setRecoveryAvailable(false);
          setRecoveryStatus("server-retry");
          updateState("interrupted");
          socket.current?.close();
          return;
        }

        if (message.type === "recording.processing") {
          setRecoveryStatus("preparing-review");
          updateState("processing");
          return;
        }

        if (message.type === "provider.error" || message.type === "error") {
          if (message.type === "provider.error") {
            if (
              !providerReady.current &&
              message.category === "canonical-live-text"
            ) {
              dispatchLifecycle({
                type: "provider.failed",
                message: "The live transcription provider did not become ready."
              });
              captureReadyReject.current?.(
                new Error("The live transcription provider did not become ready.")
              );
            }
            sendCaptureTelemetry("client.provider.failed", {
              captureAttemptId: captureAttemptId.current,
              recordingId,
              ...(message.provider ? { provider: message.provider } : {}),
              ...(message.category ? { category: message.category } : {}),
              errorCode: message.code ?? "provider_error",
              elapsedMs: Math.max(0, Date.now() - startedAt.current)
            });
            setError(liveProviderNotice(message.provider, message.category));
            return;
          }
          if (
            message.type === "error" &&
            pendingStop.current &&
            message.code &&
            TERMINAL_CAPTURE_ERROR_CODES.has(message.code)
          ) {
            sendCaptureTelemetry("client.capture.failed", {
              captureAttemptId: captureAttemptId.current,
              recordingId,
              errorCode: message.code,
              elapsedMs: Math.max(0, Date.now() - startedAt.current),
              audioPacketsSent: audioPacketsSent.current,
              transcriptFramesReceived: transcriptFramesReceived.current
            });
            shouldReconnect.current = false;
            stopSent.current = false;
            setConnection("standing-by");
            setRecoveryAvailable(true);
            setRecoveryStatus("local-evidence");
            updateState("interrupted");
            socket.current?.close();
          }
          setError(
            message.message ??
              "Live transcription is delayed. Source capture and local buffering continue."
          );
        }
      });

      ws.addEventListener("error", () => {
        setConnection("buffering");
        setError(
          "Connection interrupted. Capture continues locally; queued PCM and source audio will resume."
        );
      });

      ws.addEventListener("close", (event) => {
        socket.current = null;
        if (!providerReady.current || !firstAudioAcknowledged.current) {
          captureReadyReject.current?.(
            new Error("The secure recording connection closed before capture became ready.")
          );
        }
        if (event.code === 4008) {
          shouldReconnect.current = false;
          setError("Plan allowance reached. Capture stopped at the authorised limit.");
          stopRef.current?.();
          return;
        }
        if (pendingStop.current && captureState.current !== "review") {
          stopSent.current = false;
          analysisCommitted.current = false;
          sourceCommitted.current = false;
        }
        if (
          shouldReconnect.current &&
          captureState.current !== "review" &&
          captureState.current !== "done"
        ) {
          const reconnect = nextReconnect(reconnectAttempts.current);
          if (!reconnect) {
            shouldReconnect.current = false;
            reconnectExhausted.current = true;
            const message =
              "Live connection could not be restored after four attempts. Capture remains buffered on this device; stop now to preserve and recover it.";
            dispatchLifecycle({ type: "reconnect.exhausted", message });
            sendCaptureTelemetry("client.capture.failed", {
              captureAttemptId: captureAttemptId.current,
              recordingId,
              errorCode: "reconnect_exhausted",
              elapsedMs: Math.max(0, Date.now() - startedAt.current),
              audioPacketsSent: audioPacketsSent.current,
              transcriptFramesReceived: transcriptFramesReceived.current
            });
            return;
          }
          reconnectAttempts.current = reconnect.attempt;
          dispatchLifecycle({
            type: "reconnect.scheduled",
            attempt: reconnect.attempt
          });
          if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current);
          reconnectTimer.current = window.setTimeout(
            () => connectSocketRef.current?.(recordingId, transcriptId),
            reconnect.delayMs
          );
        }
      });
    },
    [
      beginMockTranscript,
      buildSourceBlob,
      flushBufferedPcm,
      flushBufferedSource,
      loadFinalTranscript,
      publishSourceBlob,
      sendSessionStopIfCommitted,
      sendStopWhenReady,
      markCaptureReady,
      updateState
    ]
  );

  connectSocketRef.current = connectSocket;

  const start = useCallback(async (authorityOverride?: MatterDraft["authority"]) => {
    const captureMatter = authorityOverride
      ? { ...matter, authority: authorityOverride }
      : matter;
    if (startInFlight.current || !canStartRecording(lifecycle)) return;
    startInFlight.current = true;
    startupCancelled.current = false;
    captureAttemptId.current = crypto.randomUUID();
    audioPacketsSent.current = 0;
    transcriptFramesReceived.current = 0;
    firstAudioReported.current = false;
    firstTranscriptReported.current = false;
    defaultSpeakerCluster.current = null;
    providerReady.current = false;
    firstAudioAcknowledged.current = false;
    reconnectAttempts.current = 0;
    reconnectExhausted.current = false;
    dispatchLifecycle({ type: "start.requested" });
    setRows([]);
    setGaps([]);
    setSourceSafeThroughMs(0);
    setAnalysisSafeThroughMs(0);
    setBufferedChunks(0);
    setAnalysisBufferedChunks(0);
    if (sourceAudioObjectUrl.current) {
      URL.revokeObjectURL(sourceAudioObjectUrl.current);
      sourceAudioObjectUrl.current = null;
    }
    setSourceAudioUrl(null);
    setTranscriptSession(null);
    setRecoveryStatus("unavailable");
    setRecoveryDownloads({ source: false, analysis: false });
    setPaused(false);
    setSilenceWarning(false);
    setSilenceSecondsRemaining(60);
    setLiveShare(null);
    setLiveShareBusy(false);
    pausedRef.current = false;
    pausedAt.current = 0;
    accumulatedPausedMs.current = 0;
    lastSpeechAt.current = 0;
    silenceNotificationShown.current = false;
    voiceActivity.current.reset();
    mockCursor.current = 0;
    pendingStop.current = false;
    stopSent.current = false;
    analysisCommitted.current = false;
    sourceCommitted.current = false;
    captureProducersQuiesced.current = false;
    pcmSequence.current = 0;
    pcmCursorMs.current = 0;
    sourceSequence.current = 0;
    sourceCursorMs.current = 0;
    sourceMimeType.current = "audio/webm";
    pcmWriteChain.current = Promise.resolve();
    sourceWriteChain.current = Promise.resolve();
    sourceChunks.current = [];
    pendingControls.current = [];
    pendingPcmPackets.current = new Map();
    pendingSourcePackets.current = new Map();

    let captured: MediaStream | null = null;
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("This browser does not expose secure microphone capture.");
      }
      if (
        typeof AudioContext === "undefined" ||
        typeof AudioWorkletNode === "undefined" ||
        typeof MediaRecorder === "undefined"
      ) {
        throw new Error(
          "This browser cannot provide Dicta's evidence-safe audio capture. Use a current Safari, Chrome or Edge browser."
        );
      }
      const recorderMimeType = preferredRecorderMimeType();
      if (recorderMimeType === null) {
        throw new Error(
          "This browser cannot preserve a source recording. Update the browser before recording."
        );
      }

      // Create and resume the context inside the user's direct start gesture.
      // Mobile Safari may reject a context first resumed after later awaits.
      const context = new AudioContext();
      audioContext.current = context;
      if (!context.audioWorklet) {
        throw new Error("This browser does not support AudioWorklet capture.");
      }
      // Start both permission-sensitive browser operations during the direct
      // button gesture. Awaiting notifications or storage first can make
      // mobile browsers reject or suppress microphone startup.
      const contextResume = context.resume();
      const microphoneRequest = navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      const [microphoneResult, contextResult] = await Promise.allSettled([
        microphoneRequest,
        contextResume
      ]);
      if (microphoneResult.status === "rejected") throw microphoneResult.reason;
      if (contextResult.status === "rejected") throw contextResult.reason;
      const captureStream = microphoneResult.value;
      captured = captureStream;
      stream.current = captureStream;
      if (startupCancelled.current) {
        throw new Error("Recording startup was cancelled.");
      }
      dispatchLifecycle({ type: "permission.granted" });
      if (!sandbox) {
        const buffer = await openEvidenceBuffer();
        const recovery = readActiveCapture();
        await buffer.pruneRecordings(
          recovery
            ? [recovery.recordingId, `analysis:${recovery.recordingId}`]
            : []
        );
      }

      await context.audioWorklet.addModule("/pcm-capture-worklet.js");
      const input = context.createMediaStreamSource(captureStream);
      const node = new AudioWorkletNode(context, "dicta-pcm-capture", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1]
      });
      const silent = context.createGain();
      silent.gain.value = 0;
      processor.current = node;
      input.connect(node);
      node.connect(silent);
      silent.connect(context.destination);

      let handlePcmFrame: ((samples: Float32Array) => void) | null = null;
      let firstFrameSeen = false;
      let resolveFirstFrame: (() => void) | null = null;
      const firstFrame = new Promise<void>((resolve) => {
        resolveFirstFrame = resolve;
      });
      node.port.onmessage = (event: MessageEvent<Float32Array>) => {
        const samples = event.data;
        if (!(samples instanceof Float32Array) || samples.length === 0) return;
        if (!firstFrameSeen) {
          firstFrameSeen = true;
          resolveFirstFrame?.();
        }
        handlePcmFrame?.(samples);
      };
      await Promise.race([
        firstFrame,
        new Promise<never>((_, reject) =>
          window.setTimeout(
            () => reject(new Error("The microphone opened but produced no audio frames.")),
            5_000
          )
        )
      ]);
      if (startupCancelled.current) {
        throw new Error("Recording startup was cancelled.");
      }

      const deviceLabel = captureStream.getAudioTracks()[0]?.label || "Default microphone";
      let created: {
        recording: { id: string };
        transcriptId: string;
        matterId?: string;
        sessionOrdinal?: number;
      };
      if (demoCapture) {
        const prefix = sandbox ? "sandbox" : "staging";
        created = {
          recording: { id: `${prefix}-${crypto.randomUUID()}` },
          transcriptId: `${prefix}-${crypto.randomUUID()}`
        };
      } else {
        const recordingBody = makeRecordingBody(captureMatter, deviceLabel);
        const createResponse = await fetch(
          apiUrl(
            matterId
              ? `/api/matters/${encodeURIComponent(matterId)}/recordings`
              : "/api/recordings"
          ),
          {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            matterId
              ? {
                  authorityEvidence: recordingBody.authorityEvidence,
                  deviceLabel
                }
              : recordingBody
          )
        });
        if (!createResponse.ok) {
          throw await responseError(
            createResponse,
            "The evidence session could not be created."
          );
        }
        created = (await createResponse.json()) as {
          recording: { id: string };
          transcriptId: string;
          matterId?: string;
          sessionOrdinal?: number;
        };
      }
      activeRecordingId.current = created.recording.id;
      activeTranscriptId.current = created.transcriptId;
      activeSessionOrdinal.current =
        created.sessionOrdinal ?? (options.previousSessionCount ?? 0) + 1;
      // The durable recording begins only after the local audio pipeline has
      // proven it can emit PCM frames.
      startedAt.current = Date.now();
      setRecordingStartedAt(startedAt.current);
      lastSpeechAt.current = startedAt.current;
      if (!demoCapture) {
        sendCaptureTelemetry("client.capture.started", {
          captureAttemptId: captureAttemptId.current,
          recordingId: created.recording.id,
          elapsedMs: 0,
          audioPacketsSent: 0,
          transcriptFramesReceived: 0,
          rowsDisplayed: 0
        });
      }
      if (!demoCapture) {
        const resolvedMatterId = matterId ?? created.matterId;
        if (!resolvedMatterId) {
          throw new Error("The recording session was created without a transcript link.");
        }
        writeActiveCapture({
          matterId: resolvedMatterId,
          recordingId: created.recording.id,
          transcriptId: created.transcriptId,
          matterReference: captureMatter.reference,
          startedAtEpochMs: Date.now()
        });
      }
      shouldReconnect.current = !demoCapture;
      const captureReady = demoCapture
        ? Promise.resolve()
        : new Promise<void>((resolve, reject) => {
            captureReadyResolve.current = resolve;
            captureReadyReject.current = reject;
          });
      if (demoCapture) {
        setConnection("buffering");
        beginStagingDemoTranscript();
      } else {
        connectSocket(created.recording.id, created.transcriptId);
      }

      for (const track of captureStream.getAudioTracks()) {
        track.addEventListener(
          "ended",
          () => {
            const at = activeElapsedMs();
            const gap = { startMs: at, endMs: at, reason: "Microphone became unavailable." };
            setGaps((current) => [...current, gap]);
            pendingControls.current.push({ type: "recording.gap", ...gap });
            setError("Microphone disconnected. Stop capture or select a replacement device.");
          },
          { once: true }
        );
      }

      const recorder = recorderMimeType
        ? new MediaRecorder(captureStream, { mimeType: recorderMimeType })
        : new MediaRecorder(captureStream);
      mediaRecorder.current = recorder;
      // The first emitted Blob is the canonical source declaration. Safari
      // may report a different but equivalent value on MediaRecorder.mimeType.
      sourceMimeType.current = "";
      recorder.addEventListener("dataavailable", (event) => {
        if (!event.data.size) return;
        const sequence = sourceSequence.current;
        sourceSequence.current += 1;
        const startMs = sourceCursorMs.current;
        const endMs = Math.max(startMs, activeElapsedMs());
        sourceCursorMs.current = endMs;
        const capturedAt = new Date().toISOString();
        const blob = event.data;
        sourceWriteChain.current = sourceWriteChain.current.then(async () => {
          const bytes = await blob.arrayBuffer();
          const mimeType = canonicalRecorderMimeType(
            sourceMimeType.current,
            blob.type,
            recorder.mimeType
          );
          sourceMimeType.current = mimeType;
          const chunk: ChunkRecord = {
            sequence,
            capturedAt,
            hash: await digestBytes(bytes),
            blob
          };
          sourceChunks.current.push(chunk);
          const packet: SourcePacket = {
            type: "source.append",
            sequence,
            startMs,
            endMs,
            capturedAt,
            mimeType,
            audioBase64: toBase64(new Uint8Array(bytes))
          };
          pendingSourcePackets.current.set(sequence, packet);
          await evidenceBuffer.current?.putChunk(
            {
              recordingId: created.recording.id,
              sequence,
              startMs,
              endMs,
              mimeType,
              bytes,
              capturedAt
            },
            "source"
          );
          const ws = socket.current;
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(packet));
          } else {
            setConnection("buffering");
          }
          const status = await evidenceBuffer.current?.getStatus(
            created.recording.id,
            "source"
          );
          setBufferedChunks(status?.chunkCount ?? sourceChunks.current.length);
        }).catch(() => {
          setError(
            "Compressed source writer failed. Stop capture and preserve the available source copy."
          );
        });
      });
      recorder.addEventListener("error", () => {
        setError("Compressed source writer reported an error. PCM evidence capture continues.");
      });
      recorder.addEventListener("stop", () => {
        void sourceWriteChain.current.then(() => {
          const blob = buildSourceBlob();
          if (blob) publishSourceBlob(blob);
        });
      });
      recorder.start(2000);

      // Mobile Safari fixes AudioContext to the hardware rate. Resample the
      // worklet frames explicitly so providers always receive 24 kHz PCM.
      const resampler = new StreamingLinearResampler(context.sampleRate);
      handlePcmFrame = (samples) => {
        const peak = samples.reduce((maximum, value) => Math.max(maximum, Math.abs(value)), 0);
        const rms = Math.sqrt(
          samples.reduce((sum, value) => sum + value * value, 0) /
            Math.max(1, samples.length)
        );
        const sampleDurationMs = Math.round(
          (samples.length / Math.max(1, context.sampleRate)) * 1000
        );
        if (voiceActivity.current.observe(rms, sampleDurationMs)) {
          markSpeechActive();
        }
        checkSilenceDeadline();
        setLevel(peak);

        const wallElapsed = activeElapsedMs();
        if (wallElapsed - pcmCursorMs.current > 2500) {
          const gap: CaptureGap = {
            startMs: pcmCursorMs.current,
            endMs: wallElapsed,
            reason: "Capture clock advanced without PCM frames (sleep, suspension or device loss)."
          };
          setGaps((current) => coalesceCaptureGap(current, gap).gaps);
          const control = { type: "recording.gap", ...gap };
          if (socket.current?.readyState === WebSocket.OPEN) {
            socket.current.send(JSON.stringify(control));
          } else {
            pendingControls.current.push(control);
          }
          pcmCursorMs.current = wallElapsed;
        }

        const pcm = toPcm16(resampler.process(samples));
        if (pcm.length === 0) return;
        const sequence = pcmSequence.current;
        pcmSequence.current += 1;
        const startMs = pcmCursorMs.current;
        const endMs = startMs + Math.round((pcm.length / 24000) * 1000);
        pcmCursorMs.current = endMs;
        const capturedAt = new Date().toISOString();
        const bytes = pcm.buffer.slice(0);

        pcmWriteChain.current = pcmWriteChain.current
          .then(async () => {
            const packet: PcmPacket = {
              type: "audio.append",
              sequence,
              startMs,
              endMs,
              capturedAt,
              audioBase64: toBase64(new Uint8Array(bytes))
            };
            pendingPcmPackets.current.set(sequence, packet);
            await evidenceBuffer.current?.putChunk(
              {
                recordingId: `analysis:${created.recording.id}`,
                sequence,
                startMs,
                endMs,
                mimeType: "audio/pcm;rate=24000;channels=1;bits=16",
                bytes,
                capturedAt
              },
              "analysis"
            );
            const ws = socket.current;
            if (ws?.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify(packet));
              audioPacketsSent.current += 1;
              if (!firstAudioReported.current) {
                firstAudioReported.current = true;
                sendCaptureTelemetry("client.capture.first_audio", {
                  captureAttemptId: captureAttemptId.current,
                  recordingId: created.recording.id,
                  elapsedMs: Math.max(0, Date.now() - startedAt.current),
                  audioPacketsSent: audioPacketsSent.current
                });
              }
            } else {
              setConnection("buffering");
            }
            setAnalysisBufferedChunks((current) =>
              Math.max(current, sequence + 1)
            );
          })
          .catch(() => {
            setError(
              "Analysis writer paused on a chunk. Dicta will retry buffered evidence; do not close this tab."
            );
          });
      };

      if (demoCapture) {
        captureState.current = "listening";
        dispatchLifecycle({ type: "capture.ready" });
      } else {
        await Promise.race([
          captureReady,
          new Promise<never>((_, reject) =>
            window.setTimeout(
              () => reject(new Error("Dicta could not confirm live capture within 15 seconds.")),
              15_000
            )
          )
        ]);
      }

      timer.current = window.setInterval(() => {
        const elapsed = Math.floor(activeElapsedMs() / 1000);
        setElapsedSeconds(elapsed);
        checkSilenceDeadline();
      }, 1000);
    } catch (cause) {
      const failureMessage = captureStartErrorMessage(cause);
      if (!demoCapture) {
        sendCaptureTelemetry("client.capture.failed", {
          captureAttemptId: captureAttemptId.current,
          ...(activeRecordingId.current
            ? { recordingId: activeRecordingId.current }
            : {}),
          errorCode: "capture_setup_failed",
          elapsedMs: Math.max(0, Date.now() - startedAt.current),
          audioPacketsSent: audioPacketsSent.current,
          transcriptFramesReceived: transcriptFramesReceived.current
        });
      }
      if (mediaRecorder.current?.state === "recording") {
        const stopped = new Promise<void>((resolve) => {
          mediaRecorder.current?.addEventListener("stop", () => resolve(), {
            once: true
          });
        });
        mediaRecorder.current.stop();
        await Promise.race([
          stopped,
          new Promise<void>((resolve) => window.setTimeout(resolve, 2000))
        ]);
      }
      await Promise.allSettled([
        pcmWriteChain.current,
        sourceWriteChain.current
      ]);
      captureProducersQuiesced.current = true;
      processor.current?.port.postMessage({ type: "flush" });
      processor.current?.disconnect();
      socket.current?.close();
      releaseAudioContext(audioContext);
      captured?.getTracks().forEach((track) => track.stop());
      shouldReconnect.current = false;
      captureReadyResolve.current = null;
      captureReadyReject.current = null;
      setConnection("standing-by");
      const recordingId = activeRecordingId.current;
      const [sourceStatus, analysisStatus] =
        recordingId && evidenceBuffer.current
          ? await Promise.all([
              evidenceBuffer.current.getStatus(recordingId, "source"),
              evidenceBuffer.current.getStatus(
                `analysis:${recordingId}`,
                "analysis"
              )
            ])
          : [null, null];
      if (
        recordingId &&
        ((sourceStatus?.chunkCount ?? 0) > 0 ||
          (analysisStatus?.chunkCount ?? 0) > 0)
      ) {
        const blob = buildSourceBlob();
        if (blob) publishSourceBlob(blob);
        setBufferedChunks(sourceStatus?.chunkCount ?? 0);
        setAnalysisBufferedChunks(analysisStatus?.chunkCount ?? 0);
        const asymmetric =
          captureLedgerDisposition(
            sourceStatus?.chunkCount ?? 0,
            analysisStatus?.chunkCount ?? 0
          ) === "asymmetric";
        setRecoveryAvailable(!asymmetric);
        setError(
          `${failureMessage} ${
            asymmetric
              ? "Only one capture ledger remains. Download the available evidence, then close this quarantined session."
              : "Available evidence was preserved; recover and process it."
          }`
        );
        updateState("interrupted");
      } else {
        if (!sandbox) clearActiveCapture();
        activeRecordingId.current = "";
        activeTranscriptId.current = "";
        setError(failureMessage);
        updateState("ready");
      }
      if (failureMessage.startsWith("Microphone access is blocked.")) {
        dispatchLifecycle({ type: "permission.denied", message: failureMessage });
      } else {
        dispatchLifecycle({ type: "start.failed" });
      }
    } finally {
      startInFlight.current = false;
      dispatchLifecycle({ type: "start.settled" });
    }
  }, [
    beginStagingDemoTranscript,
    buildSourceBlob,
    connectSocket,
    demoCapture,
    lifecycle,
    matter,
    matterId,
    openEvidenceBuffer,
    options.previousSessionCount,
    publishSourceBlob,
    sandbox,
    updateState
  ]);

  const publishLiveShare = useCallback(async () => {
    if (!activeRecordingId.current || captureState.current !== "listening") return;
    setLiveShareBusy(true);
    try {
      const response = await fetch(
        apiUrl(
          `/api/recordings/${encodeURIComponent(activeRecordingId.current)}/live-share`
        ),
        { method: "POST", credentials: "same-origin" }
      );
      if (!response.ok) {
        throw await responseError(response, "The live transcript link could not be created.");
      }
      setLiveShare((await response.json()) as LiveShare);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The live transcript link could not be created."
      );
    } finally {
      setLiveShareBusy(false);
    }
  }, []);

  const revokeLiveShare = useCallback(async () => {
    if (!activeRecordingId.current || !liveShare) return;
    setLiveShareBusy(true);
    try {
      const response = await fetch(
        apiUrl(
          `/api/recordings/${encodeURIComponent(activeRecordingId.current)}/live-share`
        ),
        { method: "DELETE", credentials: "same-origin" }
      );
      if (!response.ok) {
        throw await responseError(response, "The live transcript link could not be revoked.");
      }
      setLiveShare(null);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The live transcript link could not be revoked."
      );
    } finally {
      setLiveShareBusy(false);
    }
  }, [liveShare]);

  const scheduleFinalisationFallback = useCallback(() => {
    if (finaliseTimer.current) window.clearTimeout(finaliseTimer.current);
    const interruptFinalisation = (cause: unknown) => {
      console.error(
        JSON.stringify({
          event: "recording-finalisation",
          stage: "interrupted",
          recordingId: activeRecordingId.current,
          resultingState: "server-retry",
          errorCategory: "server-finalisation",
          requestId: null,
          elapsedMs: finalisationStartedAt.current
            ? Date.now() - finalisationStartedAt.current
            : null,
          detail: cause instanceof Error ? cause.message : String(cause)
        })
      );
      shouldReconnect.current = false;
      stopSent.current = false;
      setConnection("standing-by");
      setRecoveryAvailable(false);
      setRecoveryStatus("server-retry");
      setError(
        cause instanceof Error
          ? cause.message
          : "Final processing was interrupted. Source evidence remains buffered."
      );
      updateState("interrupted");
      socket.current?.close();
    };
    const attemptFinalisation = async () => {
      const recordingId = activeRecordingId.current;
      const transcriptId = activeTranscriptId.current;
      if (
        !recordingId ||
        !transcriptId ||
        captureState.current === "review" ||
        captureState.current === "interrupted"
      ) {
        return;
      }
      finalisationAttempts.current += 1;
      if (finalisationAttempts.current > MAX_FINALISATION_ATTEMPTS) {
        throw new Error(
          "The server did not acknowledge and finalise this recording after repeated retries. Source evidence remains available in this tab."
        );
      }
      if (
        captureSealPending({
          stopSent: stopSent.current,
          pendingSourcePackets: pendingSourcePackets.current.size,
          pendingAnalysisPackets: pendingPcmPackets.current.size
        })
      ) {
        await sendStopWhenReady();
        setError(
          "Waiting for the server to acknowledge both audio streams before final processing. Keep this tab open."
        );
        finaliseTimer.current = window.setTimeout(
          () => void attemptFinalisation().catch(interruptFinalisation),
          4000
        );
        return;
      }
      console.info(
        JSON.stringify({
          event: "recording-finalisation",
          stage: "provider-request",
          recordingId,
          elapsedMs: finalisationStartedAt.current
            ? Date.now() - finalisationStartedAt.current
            : 0
        })
      );
      const response = await fetch(
        apiUrl(`/api/recordings/${encodeURIComponent(recordingId)}/finalise`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}"
        }
      );
      if (!response.ok) {
        throw await responseError(response, "Final processing did not complete.");
      }
      const statusResponse = await fetch(
        apiUrl("/api/recordings/" + encodeURIComponent(recordingId)),
        { credentials: "same-origin" }
      );
      if (!statusResponse.ok) {
        throw await responseError(statusResponse, "Final processing status could not load.");
      }
      const statusView = (await statusResponse.json()) as ServerRecordingView & {
        finalisationError?: string;
      };
      if (statusView.recording?.status === "interrupted") {
        throw new Error(
          statusView.finalisationError ??
            "Final transcription failed. Source audio remains intact."
        );
      }
      if (statusView.recording?.status !== "review") {
        updateState("processing");
        finaliseTimer.current = window.setTimeout(
          () => void attemptFinalisation().catch(interruptFinalisation),
          4000
        );
        return;
      }
      const blob = buildSourceBlob();
      const audioUrl = blob ? publishSourceBlob(blob) : undefined;
      await loadFinalTranscript(transcriptId, audioUrl);
      console.info(
        JSON.stringify({
          event: "recording-finalisation",
          stage: "review-ready",
          recordingId,
          elapsedMs: finalisationStartedAt.current
            ? Date.now() - finalisationStartedAt.current
            : 0
        })
      );
    };
    finaliseTimer.current = window.setTimeout(
      () => void attemptFinalisation().catch(interruptFinalisation),
      4000
    );
  }, [
    buildSourceBlob,
    loadFinalTranscript,
    publishSourceBlob,
    sendStopWhenReady,
    updateState
  ]);

  const retryFinalisation = useCallback(async () => {
    const recordingId = activeRecordingId.current;
    if (!recordingId) {
      setError("This interrupted recording no longer has a server session to retry.");
      return;
    }
    setRecoveryStatus("preparing-review");
    recordRecoveryDiagnostic({ recordingId, resultingState: "preparing-review" });
    setRecoveryAvailable(false);
    setError(null);
    updateState("processing");
    finalisationStartedAt.current = Date.now();
    finalisationAttempts.current = 0;
    try {
      const response = await fetch(
        apiUrl(`/api/recordings/${encodeURIComponent(recordingId)}/finalise`),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: "{}"
        }
      );
      if (!response.ok) {
        throw await responseError(response, "Final processing could not be restarted.");
      }
      console.info(
        JSON.stringify({
          event: "recording-finalisation",
          stage: "server-retry-accepted",
          recordingId,
          requestId: response.headers.get("x-request-id")
        })
      );
      const statusResponse = await fetch(
        apiUrl(`/api/recordings/${encodeURIComponent(recordingId)}`),
        { credentials: "same-origin" }
      );
      if (!statusResponse.ok) {
        throw await responseError(statusResponse, "Final processing status could not load.");
      }
      const statusView = (await statusResponse.json()) as ServerRecordingView & {
        finalisationError?: string;
      };
      if (
        statusView.recording?.status === "review" ||
        statusView.recording?.status === "done" ||
        statusView.manifest !== undefined
      ) {
        const transcriptId = activeTranscriptId.current;
        if (!transcriptId) throw new Error("The prepared transcript could not be identified.");
        const blob = buildSourceBlob();
        await loadFinalTranscript(
          transcriptId,
          blob ? publishSourceBlob(blob) : undefined
        );
        return;
      }
      if (statusView.recording?.status === "interrupted") {
        throw new Error(
          statusView.finalisationError ?? "Final processing remains interrupted."
        );
      }
      stopSent.current = true;
      captureProducersQuiesced.current = true;
      scheduleFinalisationFallback();
    } catch (cause) {
      setRecoveryStatus("server-retry");
      setRecoveryAvailable(false);
      setError(
        cause instanceof Error ? cause.message : "Final processing could not be restarted."
      );
      updateState("interrupted");
    }
  }, [
    buildSourceBlob,
    loadFinalTranscript,
    publishSourceBlob,
    scheduleFinalisationFallback,
    updateState
  ]);

  const recordPauseInterval = useCallback((endedAt = Date.now()) => {
    if (pausedAt.current <= 0) return;
    const marker = {
      type: "marker.add",
      kind: pauseReason.current === "adjournment" ? "adjournment" : "note",
      atMs: pauseTimelineAt.current,
      endMs: pauseTimelineAt.current,
      label:
        pauseReason.current === "adjournment"
          ? "Adjournment"
          : pauseReason.current === "silence"
            ? "Auto-paused after silence"
            : "Recording intentionally paused",
      pauseStartedAt: new Date(pausedAt.current).toISOString(),
      pauseEndedAt: new Date(endedAt).toISOString(),
      pauseDurationMs: Math.max(0, endedAt - pausedAt.current),
      pauseReason: pauseReason.current
    };
    const ws = socket.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(marker));
    else pendingControls.current.push(marker);
  }, []);

  const stop = useCallback(async () => {
    if (startInFlight.current && canStopRecording(lifecycle)) {
      startupCancelled.current = true;
      shouldReconnect.current = false;
      dispatchLifecycle({ type: "start.cancelled" });
      captureReadyReject.current?.(new Error("Recording startup was cancelled."));
      socket.current?.close();
      stream.current?.getTracks().forEach((track) => track.stop());
      return;
    }
    if (captureState.current !== "listening" || !canStopRecording(lifecycle)) return;
    setRecoveryStatus("preparing-review");
    captureState.current = "processing";
    dispatchLifecycle({ type: "stop.requested" });
    finalisationStartedAt.current = Date.now();
    finalisationAttempts.current = 0;
    console.info(
      JSON.stringify({
        event: "recording-finalisation",
        stage: "capture-stopped",
        recordingId: activeRecordingId.current,
        elapsedMs: 0
      })
    );
    if (pausedRef.current && pausedAt.current > 0) {
      recordPauseInterval();
      accumulatedPausedMs.current += Math.max(0, Date.now() - pausedAt.current);
      pausedAt.current = 0;
    }
    setPaused(false);
    setPauseWarning(false);
    setSilenceWarning(false);
    closeSilenceNotification();
    setPauseElapsedSeconds(0);
    if (pauseWarningTimer.current) window.clearTimeout(pauseWarningTimer.current);
    if (pauseEndTimer.current) window.clearTimeout(pauseEndTimer.current);
    if (pauseClockTimer.current) window.clearInterval(pauseClockTimer.current);
    pausedRef.current = false;
    pendingStop.current = true;
    if (timer.current) window.clearInterval(timer.current);
    if (mockTimer.current) {
      window.clearInterval(mockTimer.current);
      mockTimer.current = null;
    }
    const recorderStopped =
      mediaRecorder.current && mediaRecorder.current.state !== "inactive"
        ? new Promise<void>((resolve) => {
            mediaRecorder.current?.addEventListener("stop", () => resolve(), {
              once: true
            });
            mediaRecorder.current?.stop();
          })
        : Promise.resolve();
    processor.current?.port.postMessage({ type: "flush" });
    processor.current?.disconnect();
    releaseAudioContext(audioContext);
    stream.current?.getTracks().forEach((track) => track.stop());
    setLevel(0);
    await recorderStopped;
    captureProducersQuiesced.current = true;
    if (demoCapture) {
      await Promise.allSettled([
        pcmWriteChain.current,
        sourceWriteChain.current
      ]);
      const blob = buildSourceBlob();
      if (blob) publishSourceBlob(blob);
      pendingStop.current = false;
      shouldReconnect.current = false;
      if (!sandbox) clearActiveCapture();
      setConnection("standing-by");
      const session: TranscriptSession = {
        recordingId: activeRecordingId.current,
        transcriptId: activeTranscriptId.current,
        versionId: `${sandbox ? "sandbox" : "staging"}-version-${activeTranscriptId.current}`,
        versionNumber: 1,
        state: "draft",
        recordedAtEpochMs: startedAt.current,
        ...(sourceAudioUrl ? { audioUrl: sourceAudioUrl } : {})
      };
      setTranscriptSession(session);
      if (!sandbox) onSessionChange(session);
      updateState("review");
      return;
    }
    if (reconnectExhausted.current) {
      await Promise.allSettled([pcmWriteChain.current, sourceWriteChain.current]);
      const recordingId = activeRecordingId.current;
      const [sourceStatus, analysisStatus] =
        recordingId && evidenceBuffer.current
          ? await Promise.all([
              evidenceBuffer.current.getStatus(recordingId, "source"),
              evidenceBuffer.current.getStatus(
                `analysis:${recordingId}`,
                "analysis"
              )
            ])
          : [null, null];
      const blob = buildSourceBlob();
      if (blob) publishSourceBlob(blob);
      setBufferedChunks(sourceStatus?.chunkCount ?? sourceChunks.current.length);
      setAnalysisBufferedChunks(analysisStatus?.chunkCount ?? pcmSequence.current);
      const asymmetric =
        captureLedgerDisposition(
          sourceStatus?.chunkCount ?? sourceChunks.current.length,
          analysisStatus?.chunkCount ?? pcmSequence.current
        ) === "asymmetric";
      setRecoveryAvailable(!asymmetric);
      setRecoveryStatus(asymmetric ? "quarantined" : "local-evidence");
      setConnection("standing-by");
      setError(
        asymmetric
          ? "The live connection failed and only one local capture ledger remains. Download the available evidence before closing recovery."
          : "The live connection failed. Complete source and analysis evidence remain on this device for recovery."
      );
      updateState("interrupted");
      return;
    }
    await sendStopWhenReady();
    scheduleFinalisationFallback();
  }, [
    buildSourceBlob,
    demoCapture,
    lifecycle,
    sandbox,
    publishSourceBlob,
    scheduleFinalisationFallback,
    sendStopWhenReady,
    updateState,
    onSessionChange,
    sourceAudioUrl,
    recordPauseInterval
  ]);

  const appendActionRow = useCallback(
    (kind: "exhibit" | "adjournment", label: string, atMs: number) => {
      const actionText =
        kind === "exhibit"
          ? `Exhibit — ${label}`
          : label.trim().toLowerCase() === "adjournment"
            ? "Adjournment"
            : `Adjournment — ${label}`;
      setRows((current) =>
        orderTranscriptRows([
          ...current,
          {
            id: `marker-${crypto.randomUUID()}`,
            kind,
            line: current.length + 1,
            startMs: atMs,
            endMs: atMs,
            speakerId: "unknown",
            speaker: "",
            role: "",
            baseline: actionText,
            text: actionText,
            wordConfidence: 100,
            speakerConfidence: 100,
            alignmentConfidence: 100,
            reviewScore: 100,
            reasons: [],
            provisional: false
          }
        ])
      );
    },
    []
  );

  const pause = useCallback(async (
    reason: "manual" | "silence" | "adjournment" = "manual"
  ) => {
    if (captureState.current !== "listening" || pausedRef.current) return;
    if (mediaRecorder.current?.state === "recording") {
      mediaRecorder.current.requestData();
      mediaRecorder.current.pause();
    }
    stream.current?.getAudioTracks().forEach((track) => {
      track.enabled = false;
    });
    await audioContext.current?.suspend().catch(() => undefined);
    pausedRef.current = true;
    pausedAt.current = Date.now();
    pauseTimelineAt.current = Math.round(activeElapsedMs(pausedAt.current));
    pauseReason.current = reason;
    setSilenceWarning(false);
    closeSilenceNotification();
    if (reason === "adjournment") {
      appendActionRow("adjournment", "Adjournment", pauseTimelineAt.current);
    }
    setPaused(true);
    setPauseWarning(false);
    setPauseElapsedSeconds(0);
    if (pauseClockTimer.current) window.clearInterval(pauseClockTimer.current);
    pauseClockTimer.current = window.setInterval(() => {
      setPauseElapsedSeconds(Math.floor((Date.now() - pausedAt.current) / 1000));
    }, 1000);
    pauseWarningTimer.current = window.setTimeout(() => setPauseWarning(true), 10 * 60_000);
    pauseEndTimer.current = window.setTimeout(() => stopRef.current?.(), 20 * 60_000);
    setLevel(0);
  }, [appendActionRow, closeSilenceNotification]);

  const clearPauseTimers = useCallback(() => {
    if (pauseWarningTimer.current) window.clearTimeout(pauseWarningTimer.current);
    if (pauseEndTimer.current) window.clearTimeout(pauseEndTimer.current);
    if (pauseClockTimer.current) window.clearInterval(pauseClockTimer.current);
    pauseWarningTimer.current = null;
    pauseEndTimer.current = null;
    pauseClockTimer.current = null;
  }, []);

  const keepPaused = useCallback(() => {
    if (!pausedRef.current) return;
    if (pauseEndTimer.current) window.clearTimeout(pauseEndTimer.current);
    pauseEndTimer.current = null;
    setPauseWarning(false);
  }, []);

  const resume = useCallback(async () => {
    if (captureState.current !== "listening" || !pausedRef.current) return;
    stream.current?.getAudioTracks().forEach((track) => {
      track.enabled = true;
    });
    await audioContext.current?.resume().catch(() => undefined);
    if (mediaRecorder.current?.state === "paused") {
      mediaRecorder.current.resume();
    }
    recordPauseInterval();
    accumulatedPausedMs.current += Math.max(0, Date.now() - pausedAt.current);
    pausedAt.current = 0;
    pausedRef.current = false;
    setPaused(false);
    setPauseWarning(false);
    setPauseElapsedSeconds(0);
    clearPauseTimers();
    markSpeechActive();
  }, [clearPauseTimers, markSpeechActive, recordPauseInterval]);

  const continueAfterSilence = useCallback(() => {
    if (captureState.current !== "listening" || pausedRef.current) return;
    markSpeechActive();
  }, [markSpeechActive]);

  pauseRef.current = (reason = "manual") => {
    void pause(reason);
  };

  stopRef.current = () => {
    void stop();
  };

  const downloadSource = useCallback(() => {
    const blob = buildSourceBlob();
    if (!blob) return;
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    const extension =
      blob.type.startsWith("audio/ogg")
        ? "ogg"
        : blob.type.startsWith("audio/mp4") ||
            blob.type.startsWith("audio/x-m4a")
          ? "m4a"
          : blob.type.startsWith("audio/mpeg")
            ? "mp3"
            : blob.type.startsWith("audio/wav")
              ? "wav"
              : "webm";
    anchor.download = `${matter.reference || "dicta"}-immutable-source.${extension}`;
    anchor.click();
    if (captureState.current === "interrupted") {
      setRecoveryDownloads((current) => ({ ...current, source: true }));
    }
    window.setTimeout(() => URL.revokeObjectURL(href), 0);
  }, [buildSourceBlob, matter.reference]);

  const downloadAnalysis = useCallback(async () => {
    const recordingId = activeRecordingId.current;
    if (!recordingId || !evidenceBuffer.current) return;
    try {
      const chunks = await evidenceBuffer.current.listChunks(
        `analysis:${recordingId}`,
        "analysis"
      );
      if (!chunks.length) {
        setError("No local PCM analysis chunks are available to download.");
        return;
      }
      const blob = new Blob(
        chunks.map((chunk) => chunk.bytes),
        { type: "application/octet-stream" }
      );
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = `${
        matter.reference || "dicta"
      }-analysis-24khz-mono-s16le.pcm`;
      anchor.click();
      if (captureState.current === "interrupted") {
        setRecoveryDownloads((current) => ({ ...current, analysis: true }));
      }
      window.setTimeout(() => URL.revokeObjectURL(href), 0);
    } catch {
      setError("Local PCM analysis evidence could not be prepared for download.");
    }
  }, [matter.reference]);

  const addMarker = useCallback(
    (kind: "adjournment" | "exhibit" | "note", label: string, atSeconds: number) => {
      if (captureState.current !== "listening") return;
      const marker = {
        type: "marker.add",
        kind,
        atMs: Math.max(0, Math.round(atSeconds * 1000)),
        label
      };
      const ws = socket.current;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(marker));
      } else {
        pendingControls.current.push(marker);
      }
      if (kind === "exhibit" || kind === "adjournment") {
        appendActionRow(kind, label, marker.atMs);
      }
    },
    [appendActionRow]
  );

  const recover = useCallback(async () => {
    const storedMetadata = readActiveCapture();
    const recordingId = storedMetadata?.recordingId ?? activeRecordingId.current;
    const transcriptId = storedMetadata?.transcriptId ?? activeTranscriptId.current;
    const metadata = storedMetadata ??
      (recordingId && transcriptId
        ? {
            ...(matterId ? { matterId } : {}),
            recordingId,
            transcriptId,
            matterReference: matter.reference,
            startedAtEpochMs: startedAt.current || Date.now()
          }
        : null);
    if (!metadata || !recordingId || !transcriptId) {
      setRecoveryAvailable(false);
      setRecoveryStatus("unavailable");
      setError("No interrupted recording evidence remains on this device.");
      updateState("ready");
      return;
    }
    try {
      setRecoveryStatus("recovering-local-evidence");
      recordRecoveryDiagnostic({
        recordingId,
        resultingState: "recovering-local-evidence"
      });
      const buffer = await openEvidenceBuffer();
      const [analysisChunks, recoveredSourceChunks] = await Promise.all([
        buffer.listChunks(`analysis:${metadata.recordingId}`, "analysis"),
        buffer.listChunks(metadata.recordingId, "source")
      ]);
      if (analysisChunks.length === 0 && recoveredSourceChunks.length === 0) {
        clearActiveCapture();
        setRecoveryAvailable(false);
        setRecoveryStatus("unavailable");
        recordRecoveryDiagnostic({
          recordingId,
          resultingState: "unavailable",
          errorCategory: "local-evidence-missing"
        });
        setError("No interrupted recording evidence was found. This device is ready to record.");
        updateState("ready");
        return;
      }
      if (
        captureLedgerDisposition(
          recoveredSourceChunks.length,
          analysisChunks.length
        ) === "asymmetric"
      ) {
        setBufferedChunks(recoveredSourceChunks.length);
        setAnalysisBufferedChunks(analysisChunks.length);
        setRecoveryAvailable(false);
        setRecoveryStatus("quarantined");
        setError(
          "Source and analysis ledgers are asymmetric. Finalisation is blocked; download the available evidence, then close this quarantined session."
        );
        updateState("interrupted");
        return;
      }
      pcmSequence.current = (analysisChunks.at(-1)?.sequence ?? -1) + 1;
      pcmCursorMs.current = analysisChunks.at(-1)?.endMs ?? 0;
      sourceSequence.current =
        (recoveredSourceChunks.at(-1)?.sequence ?? -1) + 1;
      sourceCursorMs.current = recoveredSourceChunks.at(-1)?.endMs ?? 0;
      sourceMimeType.current =
        recoveredSourceChunks[0]?.mimeType ?? "audio/webm";
      const lastSafeMs = Math.min(
        pcmCursorMs.current,
        sourceCursorMs.current
      );
      const elapsedMs = Math.max(
        pcmCursorMs.current,
        sourceCursorMs.current,
        Date.now() - metadata.startedAtEpochMs
      );
      if (elapsedMs > lastSafeMs) {
        const gap = {
          type: "recording.gap",
          startMs: lastSafeMs,
          endMs: elapsedMs,
          reason: "Browser page closed or crashed; no audio was captured before recovery."
        };
        pendingControls.current.push(gap);
        setGaps((current) => [
          ...current,
          {
            startMs: lastSafeMs,
            endMs: elapsedMs,
            reason: gap.reason
          }
        ]);
      }
      startedAt.current = metadata.startedAtEpochMs;
      setElapsedSeconds(Math.floor(elapsedMs / 1000));
      pendingStop.current = true;
      stopSent.current = false;
      analysisCommitted.current = false;
      sourceCommitted.current = false;
      captureProducersQuiesced.current = true;
      shouldReconnect.current = true;
      setConnection("buffering");
      setError("Recovered local evidence is uploading. The interruption remains explicit.");
      updateState("processing");
      connectSocketRef.current?.(metadata.recordingId, metadata.transcriptId);
      scheduleFinalisationFallback();
    } catch (cause) {
      setRecoveryAvailable(false);
      setRecoveryStatus("error");
      recordRecoveryDiagnostic({
        recordingId,
        resultingState: "error",
        errorCategory: "local-evidence-open"
      });
      setError(
        cause instanceof Error
          ? cause.message
          : "Recovered evidence could not be prepared for upload."
      );
      updateState("interrupted");
    }
  }, [
    matter.reference,
    matterId,
    openEvidenceBuffer,
    scheduleFinalisationFallback,
    updateState
  ]);

  const resetRecovery = useCallback(() => {
    if (!recoveryDownloaded) return;
    clearActiveCapture();
    activeRecordingId.current = "";
    activeTranscriptId.current = "";
    pendingControls.current = [];
    pendingPcmPackets.current = new Map();
    pendingSourcePackets.current = new Map();
    setRecoveryAvailable(false);
    setRecoveryStatus("unavailable");
    setRecoveryDownloads({ source: false, analysis: false });
    setBufferedChunks(0);
    setAnalysisBufferedChunks(0);
    setError(
      "Recovery closed after evidence download. The encrypted local buffer was not deleted."
    );
    updateState("ready");
  }, [recoveryDownloaded, updateState]);

  const retryRecovery = useCallback(() => {
    setRecoveryStatus("checking");
    setRecoveryAvailable(false);
    setError(null);
    setRecoveryCheckNonce((current) => current + 1);
  }, []);

  useEffect(
    () => () => {
      shouldReconnect.current = false;
      if (timer.current) window.clearInterval(timer.current);
      if (mockTimer.current) window.clearInterval(mockTimer.current);
      if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current);
      if (finaliseTimer.current) window.clearTimeout(finaliseTimer.current);
      if (pauseWarningTimer.current) window.clearTimeout(pauseWarningTimer.current);
      if (pauseEndTimer.current) window.clearTimeout(pauseEndTimer.current);
      if (pauseClockTimer.current) window.clearInterval(pauseClockTimer.current);
      closeSilenceNotification();
      stream.current?.getTracks().forEach((track) => track.stop());
      socket.current?.close();
      releaseAudioContext(audioContext);
      if (sourceAudioObjectUrl.current) {
        URL.revokeObjectURL(sourceAudioObjectUrl.current);
        sourceAudioObjectUrl.current = null;
      }
      evidenceBuffer.current?.close();
    },
    []
  );

  useEffect(() => {
    if (sandbox) return;
    if (STAGING_DEMO) {
      clearActiveCapture();
      setRecoveryStatus("unavailable");
      return;
    }
    const metadata = readActiveCapture();
    let cancelled = false;
    void (async () => {
      try {
        const buffer = await openEvidenceBuffer();
        if (cancelled) return;
        await buffer.pruneRecordings(
          metadata
            ? [metadata.recordingId, `analysis:${metadata.recordingId}`]
            : []
        );
        if (!metadata) {
          setRecoveryStatus("unavailable");
          return;
        }
        const [source, analysis, recordingResponse] = await Promise.all([
          buffer.listChunks(metadata.recordingId, "source"),
          buffer.listChunks(`analysis:${metadata.recordingId}`, "analysis"),
          fetch(
            apiUrl(
              `/api/recordings/${encodeURIComponent(metadata.recordingId)}`
            )
          )
        ]);
        activeRecordingId.current = metadata.recordingId;
        activeTranscriptId.current = metadata.transcriptId;
        startedAt.current = metadata.startedAtEpochMs;
        setRecordingStartedAt(metadata.startedAtEpochMs);
        const recordingView = recordingResponse.ok
          ? ((await recordingResponse.json()) as ServerRecordingView)
          : null;
        const resolvedMatterId =
          metadata.matterId ?? recordingView?.recording?.matterId;
        if (resolvedMatterId && resolvedMatterId !== metadata.matterId) {
          writeActiveCapture({ ...metadata, matterId: resolvedMatterId });
        }
        if (!source.length && !analysis.length) {
          const serverAlreadyFinalised =
            recordingView?.recording?.status === "review" ||
            recordingView?.recording?.status === "done" ||
            recordingView?.manifest !== undefined;
          if (serverAlreadyFinalised) {
            await loadFinalTranscript(metadata.transcriptId);
            return;
          }
          if (
            recordingResponse.ok &&
            recordingView?.recording?.status === "interrupted"
          ) {
            setRecoveryAvailable(false);
            setRecoveryStatus("server-retry");
            recordRecoveryDiagnostic({
              recordingId: metadata.recordingId,
              resultingState: "server-retry",
              errorCategory: "server-interrupted",
              requestId: recordingResponse.headers.get("x-request-id")
            });
            setError(
              "No local evidence remains, but the server session can retry preparing Review."
            );
            updateState("interrupted");
            return;
          }
          if (recordingResponse.status === 404 || recordingResponse.status === 410) {
            clearActiveCapture();
            recordRecoveryDiagnostic({
              recordingId: metadata.recordingId,
              resultingState: "unavailable",
              errorCategory: "stale-marker",
              requestId: recordingResponse.headers.get("x-request-id")
            });
            setError("No interrupted recording evidence was found. This device is ready to record.");
          } else {
            setError(
              "Dicta could not verify this recovery marker. Check the server again before clearing it."
            );
          }
          setRecoveryAvailable(false);
          setRecoveryStatus("unavailable");
          return;
        }
        // Hydrate local evidence before server ownership verification. A
        // transient server failure must not hide download/release controls.
        pcmSequence.current = (analysis.at(-1)?.sequence ?? -1) + 1;
        pcmCursorMs.current = analysis.at(-1)?.endMs ?? 0;
        sourceSequence.current = (source.at(-1)?.sequence ?? -1) + 1;
        sourceCursorMs.current = source.at(-1)?.endMs ?? 0;
        sourceMimeType.current = source[0]?.mimeType ?? "audio/webm";
        sourceChunks.current = source.map((chunk) => ({
          sequence: chunk.sequence,
          capturedAt: chunk.capturedAt,
          hash: chunk.sha256,
          blob: new Blob([chunk.bytes], { type: chunk.mimeType })
        }));
        setBufferedChunks(source.length);
        setAnalysisBufferedChunks(analysis.length);
        if (matterId && resolvedMatterId && resolvedMatterId !== matterId) {
          setRecoveryStatus("unavailable");
          setRecoveryAvailable(false);
          setError(
            "This device evidence belongs to another transcript. Return to transcripts and open the unfinished capture from the device notice."
          );
          updateState("interrupted");
          return;
        }
        if (matterId && !resolvedMatterId && !recordingResponse.ok) {
          setRecoveryStatus("unavailable");
          setRecoveryAvailable(false);
          setError(
            "Dicta could not verify this local evidence with the server. Check again; if it remains unavailable, download every available copy before releasing this device."
          );
          updateState("interrupted");
          return;
        }
        const blob = buildSourceBlob();
        let audioUrl = blob ? publishSourceBlob(blob) : undefined;
        const serverAlreadyFinalised =
          recordingView?.recording?.status === "review" ||
          recordingView?.recording?.status === "done" ||
          recordingView?.manifest !== undefined;
        if (serverAlreadyFinalised) {
          if (
            recordingView?.sourceAudio?.retentionPolicy === "delete-within-24h"
          ) {
            await clearLocalRecordingAudio(metadata.recordingId);
            audioUrl = undefined;
          }
          if (
            recordingView?.manifest !== undefined &&
            recordingView.recording?.status !== "review" &&
            recordingView.recording?.status !== "done"
          ) {
            await fetch(
              apiUrl(
                `/api/recordings/${encodeURIComponent(metadata.recordingId)}/finalise`
              ),
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: "{}"
              }
            ).catch(() => undefined);
          }
          await loadFinalTranscript(metadata.transcriptId, audioUrl);
          return;
        }
        const asymmetric =
          captureLedgerDisposition(source.length, analysis.length) ===
          "asymmetric";
        setRecoveryAvailable(recordingResponse.ok && !asymmetric);
        setRecoveryStatus(
          recordingResponse.ok && !asymmetric
            ? "local-evidence"
            : recordingResponse.ok
              ? "quarantined"
              : "unavailable"
        );
        recordRecoveryDiagnostic({
          recordingId: metadata.recordingId,
          resultingState:
            recordingResponse.ok && !asymmetric
              ? "local-evidence"
              : recordingResponse.ok
                ? "quarantined"
                : "unavailable",
          errorCategory: asymmetric
            ? "asymmetric-evidence"
            : recordingResponse.ok
              ? undefined
              : "server-unavailable",
          requestId: recordingResponse.headers.get("x-request-id")
        });
        setError(
          recordingResponse.ok && !asymmetric
            ? "Unfinished capture recovered from encrypted local storage. Recover and process it; the interruption will be disclosed."
            : recordingResponse.ok
              ? "Only one capture ledger remains. Download the available evidence, then close this quarantined session."
            : "Local evidence was recovered, but the server session is unavailable. Download every available copy before starting again."
        );
        captureProducersQuiesced.current = true;
        updateState("interrupted");
      } catch {
        setError(
          "An unfinished capture was found, but its encrypted buffer could not be opened."
        );
        setRecoveryAvailable(false);
        setRecoveryStatus("error");
        updateState("interrupted");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    buildSourceBlob,
    clearLocalRecordingAudio,
    loadFinalTranscript,
    matterId,
    openEvidenceBuffer,
    publishSourceBlob,
    recoveryCheckNonce,
    sandbox,
    updateState
  ]);

  useEffect(() => {
    const check = () => checkSilenceDeadline();
    const onVisibility = () => {
      if (!document.hidden) check();
    };
    const onServiceWorkerMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; action?: string } | null;
      if (data?.type !== "dicta:silence-action") return;
      if (data.action === "pause") pauseRef.current?.("silence");
      if (data.action === "continue") continueAfterSilence();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisibility);
    navigator.serviceWorker?.addEventListener("message", onServiceWorkerMessage);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisibility);
      navigator.serviceWorker?.removeEventListener("message", onServiceWorkerMessage);
    };
  }, [checkSilenceDeadline, continueAfterSilence]);

  useEffect(() => {
    const requestWakeLock = async () => {
      if (state !== "listening" || document.hidden) return;
      const wakeLockApi = (navigator as Navigator & {
        wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
      }).wakeLock;
      if (!wakeLockApi || wakeLock.current) return;
      try {
        wakeLock.current = await wakeLockApi.request("screen");
      } catch {
        // Wake lock is an enhancement; explicit gap evidence still protects capture.
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt.current = Date.now();
        return;
      }
      const hiddenSince = hiddenAt.current;
      hiddenAt.current = null;
      if (state === "listening" && hiddenSince) {
        const returnedAt = Date.now();
        const duration = returnedAt - hiddenSince;
        if (duration > 1_000) {
          const endMs = activeElapsedMilliseconds(
            startedAt.current,
            returnedAt,
            accumulatedPausedMs.current,
            pausedRef.current ? pausedAt.current : 0
          );
          const gap: CaptureGap = {
            startMs: Math.max(0, endMs - duration),
            endMs,
            reason: "Browser was backgrounded; mobile operating systems may suspend microphone capture."
          };
          setGaps((current) => [...current, gap]);
          const control = { type: "recording.gap", ...gap };
          if (socket.current?.readyState === WebSocket.OPEN) {
            socket.current.send(JSON.stringify(control));
          } else {
            pendingControls.current.push(control);
          }
        }
        void audioContext.current?.resume().catch(() => undefined);
        void requestWakeLock();
      }
    };
    if (state === "listening") void requestWakeLock();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      const held = wakeLock.current;
      wakeLock.current = null;
      if (held) void held.release().catch(() => undefined);
    };
  }, [state]);

  useEffect(() => {
    onCaptureStateChange?.(state);
    if (state !== "listening" && state !== "processing") return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [onCaptureStateChange, state]);

  return {
    state,
    paused,
    silenceWarning,
    silenceSecondsRemaining,
    pauseWarning,
    pauseElapsedSeconds,
    recordingStartedAt,
    liveShare,
    liveShareBusy,
    isStarting,
    startupStage,
    elapsedSeconds,
    level,
    rows,
    sourceSafeThroughMs,
    analysisSafeThroughMs,
    bufferedChunks,
    analysisBufferedChunks,
    connection,
    gaps,
    error,
    storageWarning,
    transcriptSession,
    sourceAudioUrl,
    recoveryAvailable,
    recoveryStatus,
    recoveryEvidence,
    recoveryDownloads,
    recoveryDownloaded,
    canReleaseRecovery: recoveryDownloaded,
    start,
    pause,
    resume,
    keepPaused,
    continueAfterSilence,
    stop,
    recover,
    resetRecovery,
    retryRecovery,
    retryFinalisation,
    addMarker,
    downloadSource,
    downloadAnalysis,
    publishLiveShare,
    revokeLiveShare
  };
}
